package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * derived 派生值专属语义校验：
 *
 * 1. **禁止 await**：derived 仅用于纯派生计算，表达式内出现 [ExprAst.AwaitExpr] 报 [DslError.E205_DERIVED_FORBIDDEN_EXPR]；
 * 2. **循环依赖**：根据每条 derived 表达式中引用的其它 derived（[ExprAst.DerivedRef]）构建依赖图，
 *    通过 DFS 检测环，命中报 [DslError.E204_DERIVED_CYCLE]（含自引用 a → a）。
 *
 * 其余引用合法性（state / derived 名是否声明）由 [ReferenceResolver] 负责。
 */
class DerivedExprChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        val derived = screen.derived ?: return

        // 记录每个 derived 名的声明位置，供环检测报错定位。
        positions = derived.entries.associate { it.name to it.position }

        // 1. 禁止 await：逐条扫描表达式。
        derived.entries.forEach { entry ->
            forbidAwait(entry.expr)
        }

        // 2. 循环依赖：构建 name → 直接依赖的 derived 名集合，DFS 找环。
        val deps: Map<String, Set<String>> = derived.entries.associate { entry ->
            entry.name to collectDerivedRefs(entry.expr)
        }
        detectCycles(derived.entries.map { it.name }, deps)
    }

    /** 递归扫描表达式，遇 [ExprAst.AwaitExpr] 报 E205。 */
    private fun forbidAwait(expr: ExprAst) {
        if (expr is ExprAst.AwaitExpr) {
            diag.error(DslError.E205_DERIVED_FORBIDDEN_EXPR, expr.position, "await")
            return
        }
        walkChildren(expr) { forbidAwait(it) }
    }

    /** 收集表达式中引用到的所有 derived 名（直接依赖）。 */
    private fun collectDerivedRefs(expr: ExprAst): Set<String> {
        val out = linkedSetOf<String>()
        fun walk(e: ExprAst) {
            if (e is ExprAst.DerivedRef) out += e.name
            walkChildren(e) { walk(it) }
        }
        walk(expr)
        return out
    }

    /** 基于依赖图做环检测；命中环时按首个发现的环路径报 E204。 */
    private fun detectCycles(names: List<String>, deps: Map<String, Set<String>>) {
        // 0=未访问，1=在当前 DFS 栈中，2=已完成
        val state = HashMap<String, Int>()
        val stack = ArrayDeque<String>()
        val reported = HashSet<String>()

        fun dfs(name: String): Boolean {
            state[name] = 1
            stack.addLast(name)
            for (dep in deps[name].orEmpty()) {
                // 仅在 deps 图内的名字才参与（非 derived 名由 ReferenceResolver 兜底）
                if (dep !in deps) continue
                val s = state[dep] ?: 0
                if (s == 1) {
                    // 找到环：截取 stack 中 dep..name 的路径
                    val idx = stack.indexOf(dep)
                    val cycle = stack.toList().subList(idx, stack.size) + dep
                    // sorted() 与原 toSortedSet 同为字典序遍历，join 结果逐字符一致（FR-UI-061 中立化）
                    val key = cycle.toSet().sorted().joinToString(",")
                    if (reported.add(key)) {
                        diag.error(DslError.E204_DERIVED_CYCLE, positionOf(name), cycle.joinToString(" → "))
                    }
                    return true
                }
                if (s == 0 && dfs(dep)) {
                    // 环已在更深层报告，向上中断当前路径但不重复报
                    stack.removeLast()
                    state[name] = 2
                    return true
                }
            }
            stack.removeLast()
            state[name] = 2
            return false
        }

        for (name in names) {
            if ((state[name] ?: 0) == 0) dfs(name)
        }
    }

    private var positions: Map<String, top.wcpe.allincore.dsl.compiler.source.SourcePosition> = emptyMap()

    private fun positionOf(name: String): top.wcpe.allincore.dsl.compiler.source.SourcePosition =
        positions[name] ?: top.wcpe.allincore.dsl.compiler.source.SourcePosition.UNKNOWN

    /**
     * 遍历表达式的直接子表达式，对每个子节点调用 [visit]。
     *
     * 只覆盖 derived 表达式实际可能出现的节点；叶子节点（Literal / *Ref / ThemeRef 等）无子节点。
     */
    private inline fun walkChildren(expr: ExprAst, visit: (ExprAst) -> Unit) {
        when (expr) {
            is ExprAst.Interp -> expr.parts.forEach { if (it is ExprAst.Interp.InterpPart.ExprPart) visit(it.expr) }
            is ExprAst.BinaryOp -> { visit(expr.left); visit(expr.right) }
            is ExprAst.UnaryOp -> visit(expr.operand)
            is ExprAst.MemberAccess -> visit(expr.target)
            is ExprAst.SafeMemberAccess -> visit(expr.target)
            is ExprAst.MemberCall -> { visit(expr.target); expr.args.forEach(visit); expr.namedArgs.forEach { visit(it.value) } }
            is ExprAst.SafeMemberCall -> { visit(expr.target); expr.args.forEach(visit) }
            is ExprAst.Elvis -> { visit(expr.left); visit(expr.right) }
            is ExprAst.Ternary -> { visit(expr.cond); visit(expr.thenExpr); visit(expr.elseExpr) }
            is ExprAst.IfExpr -> {
                visit(expr.cond)
                expr.thenBody.forEach { stmtExpr(it, visit) }
                expr.elseBody.forEach { stmtExpr(it, visit) }
            }
            is ExprAst.WhenExpr -> {
                visit(expr.subject)
                expr.branches.forEach { br -> br.values.forEach(visit); br.body.forEach { stmtExpr(it, visit) } }
                expr.elseBody?.forEach { stmtExpr(it, visit) }
            }
            is ExprAst.IsExpr -> visit(expr.target)
            is ExprAst.IndexExpr -> { visit(expr.target); visit(expr.index) }
            is ExprAst.RangeExpr -> { visit(expr.start); visit(expr.endValue) }
            is ExprAst.ListLiteralExpr -> expr.elements.forEach(visit)
            is ExprAst.MapLiteralExpr -> expr.entries.forEach { visit(it.value) }
            is ExprAst.CallExpr -> { visit(expr.callee); expr.args.forEach(visit); expr.namedArgs.forEach { visit(it.value) } }
            is ExprAst.BuiltinCallExpr -> { expr.args.forEach(visit); expr.namedArgs.forEach { visit(it.value) } }
            is ExprAst.AwaitExpr -> { expr.netRequestCall?.let(visit); expr.suspendCall?.let(visit) }
            else -> Unit
        }
    }

    /** IfExpr / WhenExpr 分支体里的 ExprStmt 也可能含子表达式，仅递归这种最常见形态。 */
    private inline fun stmtExpr(stmt: top.wcpe.allincore.dsl.compiler.ast.StmtAst, visit: (ExprAst) -> Unit) {
        if (stmt is top.wcpe.allincore.dsl.compiler.ast.StmtAst.ExprStmt) visit(stmt.call)
    }
}
